import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { api, jobId } from "@/lib/api";

export const useJobs = () => {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(Array.isArray(r.data) ? r.data : [])).catch(() => setJobs([]));
  }, []);
  return jobs;
};

export const JobRow = ({ job, i }) => (
  <Link to={`/careers/${jobId(job)}`} data-testid={`job-row-${i}`} className="group grid items-center gap-3 border-b border-white/10 py-6 transition-colors duration-300 hover:bg-white/[0.03] md:grid-cols-12 md:px-4">
    <span className="font-mono text-xs uppercase tracking-[0.16em] text-[#E3BC6E] md:col-span-3">{job?.department}</span>
    <span className="font-display text-2xl font-bold uppercase md:col-span-5 md:text-3xl">{job?.title}</span>
    <span className="text-sm text-slate-300 md:col-span-3">{job?.location}</span>
    <ArrowUpRight className="h-6 w-6 justify-self-start text-slate-300 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#D4A24C] md:col-span-1 md:justify-self-end" />
  </Link>
);

export const JobBoard = () => {
  const jobs = useJobs();
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("All");
  const depts = useMemo(() => ["All", ...new Set((jobs ?? []).map((j) => j.department))], [jobs]);
  const list = (jobs ?? []).filter((j) => (dept === "All" || j.department === dept) && `${j.title} ${j.summary}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="job-board">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <label className="flex h-14 w-full items-center gap-3 border border-white/15 bg-[#0B1D4F] px-4 md:max-w-md">
          <Search className="h-5 w-5 text-slate-300" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roles" data-testid="job-search-input" className="w-full bg-transparent text-base outline-none placeholder:text-slate-400" />
        </label>
        <div className="flex flex-wrap gap-2">
          {depts.map((d) => (
            <button key={d} onClick={() => setDept(d)} data-testid={`job-filter-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`border px-3 py-2 text-sm transition-colors ${dept === d ? "border-[#001870] bg-[#001870] text-white" : "border-white/15 text-slate-200 hover:border-white/50"}`}>{d}</button>
          ))}
        </div>
      </div>
      <p data-testid="job-count" className="mt-8 font-mono text-xs uppercase tracking-[0.16em] text-slate-300">{jobs === null ? "Loading roles…" : `${list.length} open role${list.length === 1 ? "" : "s"}`}</p>
      <div className="mt-2 border-t border-white/10">
        {list.map((j, i) => <JobRow key={jobId(j)} job={j} i={i} />)}
        {jobs !== null && list.length === 0 && (
          <p data-testid="job-empty" className="py-10 text-slate-300">No matching open roles. <Link to="/contact?audience=careers" className="text-[#E3BC6E] underline">Send a general inquiry</Link>.</p>
        )}
      </div>
    </div>
  );
};
